import React from "react";
import { useTranslation } from "react-i18next";
import ScreenLayout from "../ui/ScreenLayout";
import Button from "../ui/Button";
import Icon from "../ui/Icon";
import { formatDecimal, formatEuros } from "../../utils/format";

export const PREAUTH_AMOUNT = 40;

const SmallSpinner = () => (
  <span
    className="h-[18px] w-[18px] shrink-0 rounded-full border-2 border-qv-brand-100 border-t-qv-brand animate-spin"
    aria-hidden="true"
  />
);

// Écran 1e : recharge terminée. Le montant n'est connu qu'une fois la recharge
// valorisée ; d'ici là il reste « en cours de calcul ». Le bouton de téléchargement
// n'apparaît qu'une fois le justificatif disponible : avant, on annonce l'envoi par e-mail.
export default function ChargeCompletedScreen({
  amount,
  energyKwh,
  duration,
  email,
  receiptReady,
  onDownloadReceipt,
  isDownloadingReceipt,
  receiptError,
  unlockAction,
  unlockStatus,
  panel,
}) {
  const { t, i18n } = useTranslation();
  const released = amount != null ? Math.max(PREAUTH_AMOUNT - amount, 0) : null;

  return (
    <ScreenLayout footer={unlockAction} panel={panel}>
      <div className="pt-2 flex flex-col items-center md:pt-0">
        <div className="h-16 w-16 rounded-full bg-qv-green-50 flex items-center justify-center">
          <Icon name="check-circle-bold" size={36} className="text-qv-green md:h-10 md:w-10" />
        </div>
        <h1 className="mt-4 text-[26px] leading-[34px] tracking-[-.02em] font-bold text-center md:text-[30px] md:leading-[38px]">
          {t("startCharging.completed.title")}
        </h1>
      </div>

      <div className="mt-5 rounded-2xl border border-qv-line p-5 md:mt-6">
        <div className="flex items-center justify-between gap-4 pb-4 border-b border-qv-line">
          <span className="text-sm font-medium text-qv-muted">{t("startCharging.completed.amountDebited")}</span>
          {amount != null ? (
            <span className="text-[28px] font-bold tabular-nums">{formatEuros(amount, i18n.language)}</span>
          ) : (
            <span className="flex items-center gap-2 text-base font-semibold text-qv-muted">
              <SmallSpinner />
              {t("startCharging.completed.amountPending")}
            </span>
          )}
        </div>
        <div className="mt-4 flex justify-between text-sm">
          <span className="text-qv-muted">{t("startCharging.completed.energy")}</span>
          <b>{formatDecimal(energyKwh, i18n.language)} kWh</b>
        </div>
        <div className="mt-2 flex justify-between text-sm">
          <span className="text-qv-muted">{t("startCharging.completed.duration")}</span>
          <b>{duration}</b>
        </div>
        {released != null ? (
          <div className="mt-3.5 rounded-lg bg-qv-green-50 px-3 py-2.5 text-xs leading-[18px] font-semibold text-qv-green-900">
            {t("startCharging.completed.released", { amount: formatEuros(released, i18n.language) })}
          </div>
        ) : (
          <div className="mt-3.5 rounded-lg bg-qv-brand-50 px-3 py-2.5 text-xs leading-[18px] font-semibold text-qv-brand-900">
            {t("startCharging.completed.amountPendingReleased")}
          </div>
        )}
      </div>

      <div className="mt-4 rounded-2xl border border-qv-line p-4">
        <div className="flex items-center gap-2.5">
          <Icon name="document-text-linear" size={22} className="text-qv-brand" />
          <span className="text-[15px] font-bold md:text-sm md:font-semibold">{t("startCharging.completed.receiptTitle")}</span>
        </div>
        {!receiptReady ? (
          <p className="mt-1.5 text-[13px] leading-[19px] font-medium text-qv-muted break-words">
            {email
              ? t("startCharging.completed.receiptPending", { email })
              : t("startCharging.completed.receiptPendingNoEmail")}
          </p>
        ) : (
          <>
            <p className="mt-1.5 mb-3 text-[13px] leading-[19px] font-medium text-qv-muted break-words">
              {email
                ? t("startCharging.completed.receiptSent", { email })
                : t("startCharging.completed.receiptSentNoEmail")}
            </p>
            {receiptError && !isDownloadingReceipt && (
              <p className="mb-2.5 flex gap-2 items-start text-[13px] leading-[19px] font-medium text-qv-red" role="alert">
                <Icon name="danger-circle-linear" size={18} />
                {receiptError}
              </p>
            )}
            {isDownloadingReceipt ? (
              <div className="h-11 rounded-lg border-[1.5px] border-qv-line flex items-center justify-center gap-2 text-sm font-semibold text-qv-muted" role="status">
                <SmallSpinner />
                {t("startCharging.completed.preparing")}
              </div>
            ) : (
              <Button
                variant="secondary"
                icon={receiptError ? "refresh-linear" : "download-minimalistic-linear"}
                className="!h-11 !text-sm"
                onClick={onDownloadReceipt}
              >
                {receiptError ? t("startCharging.completed.retry") : t("startCharging.completed.download")}
              </Button>
            )}
          </>
        )}
      </div>

      {unlockStatus}
    </ScreenLayout>
  );
}
